import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component } from 'react';

import { connect } from 'react-redux';
import {
    LineSegment,
    VictoryAxis,
    VictoryChart,
    VictoryCursorContainer,
    VictoryLabel,
    VictoryLegend,
    VictoryLine,
    VictoryScatter,
} from 'victory';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { isMobile } from '/@/utils';

const colors = {
    primary: '#A27749',
    primaryDark: '#5B350C',
    grayLight: '#E5E5E5',
    gray: '#BFBFBF',
    grayDark: '#545454',
    palette: [
        ['#6ADFFF', '#00BBEF'],
        ['#FFD862', '#E1A900'],
        ['#FF9365', '#E54500'],
        ['#9586D8', '#4B36A7'],
    ],
};

const textStyles = {
    fill: colors.grayDark,
    fontSize: 15,
    fontFamily: 'inherit',
};

@connect((state) => {
    const country = getSessionCountry(state);
    return { countryCode: country && country.code };
})
@autobind
export default class CornDensityChart extends Component {
    static contextTypes = {
        print: PropTypes.bool,
        units: PropTypes.object,
    };

    static defaultProps = {
        importantDots: true,
        importantReferences: true,
    };

    state = {
        width: (this.context.print && 970) || 0,
        height: (this.context.print && 350) || 0,
    };

    componentDidMount() {
        this.setDimensions();
    }

    UNSAFE_componentWillReceiveProps() {
        this.setDimensions();
    }

    setDimensions() {
        const parentNode = this.rootNode.parentNode;
        const parentClientRect = parentNode.getBoundingClientRect();
        const height = isMobile()
            ? Math.max(350 + 80, parentClientRect.width / 2)
            : Math.max(350 + 80, parentClientRect.height); // +80 due to legend

        return this.setState({ height, width: parentClientRect.width });
    }

    getProductColor(product, index) {
        if (product.type === 'worse') return 'red';
        else if (product.type === 'better') return 'green';
        else if (product.type === 'selected') return colors.grayDark;
        else if (product.selected) return colors.palette[index][1];
        else return colors.palette[index][0];
    }

    parseData(products) {
        const selectedProducts = [];
        const importantPoints = [];
        let maxY = 0;
        products.forEach((product) => {
            const lineData = [];
            const name =
                product.catalogName ||
                (product.profile ? product.profile.product.catalogName : product.product.catalogName);

            if (product.estimatedYieldByHarvestDensity) {
                product.estimatedYieldByHarvestDensity.forEach((point) => {
                    lineData.push({
                        x: point.density,
                        y: point.estimatedYield,
                    });
                    if (point.estimatedYield > maxY) maxY = point.estimatedYield;
                });
            }

            if (lineData.length > 0) {
                selectedProducts.push({
                    id: product.id,
                    label: name,
                    selected: product.selected,
                    userDensity: product.harvestDensity.user,
                    lineData,
                    type: product.type,
                });

                if (product.harvestDensity.recommended) {
                    importantPoints.push({
                        x: product.harvestDensity.recommended,
                        y: product.estimatedYield.forRecommendedHarvestDensity,
                    });
                }
            }
        });

        // Approximate padding value based on max Y character count.
        const leftPadding = Math.max(50, Math.round(maxY).toString().length * 12);
        const padding = { top: 30, bottom: 30 + 80, left: leftPadding, right: 0 }; // +80 due to legend

        // Sort by selected so that lines for selected products render on top.
        // products.sort((a, b) => a.selected ? 1 : -1); // eslint-disable-line no-unused-vars
        return { selectedProducts, importantPoints, padding };
    }

    calculateLabelPositions({ chartWidth, chartHeight, padding }) {
        return {
            xLabelPosition: {
                x: chartWidth,
                y: chartHeight - padding.bottom - 10,
            },
            yLabelPosition: {
                x: padding.left + 10,
                y: 20,
            },
        };
    }

    formatDensity(value) {
        return this.context.units.toDensity(value, 'corn').localeValue;
    }

    formatYield(value) {
        return this.context.units.toYield(value, 'corn').localeValue;
    }

    setRooNodeRef(node) {
        this.rootNode = node;
    }

    renderXAxis({ xLabelPosition }) {
        return (
            <VictoryAxis
                fixLabelOverlap
                label={`${_('Densidad')} (${this.context.units.getUnit('density', 'corn')})`}
                axisLabelComponent={<VictoryLabel {...xLabelPosition} />}
                tickFormat={this.formatDensity}
                style={{
                    axis: {
                        stroke: colors.grayLight,
                        strokeWidth: 2,
                    },
                    axisLabel: {
                        ...textStyles,
                        textAnchor: 'end',
                        verticalAnchor: 'end',
                    },
                    tickLabels: {
                        ...textStyles,
                        textAnchor: (axis) => {
                            if (axis.ticks[0] === axis.tick) return 'start';
                            if (axis.ticks[axis.ticks.length - 1] === axis.tick) return 'end';
                            return 'middle';
                        },
                    },
                }}
            />
        );
    }

    renderYAxis({ yLabelPosition }) {
        return (
            <VictoryAxis
                dependentAxis
                fixLabelOverlap
                label={`${_('Rendimiento estimado')} (${this.context.units.getUnit('yield', 'corn')})`}
                axisLabelComponent={<VictoryLabel {...yLabelPosition} angle={0} />}
                tickFormat={this.formatYield}
                style={{
                    grid: {
                        stroke: colors.grayLight,
                    },
                    axis: {
                        stroke: colors.grayLight,
                        strokeWidth: 2,
                    },
                    axisLabel: {
                        ...textStyles,
                        textAnchor: 'start',
                    },
                    tickLabels: {
                        ...textStyles,
                    },
                }}
            />
        );
    }

    renderImportantDots({ importantPoints }) {
        return (
            <VictoryScatter
                size={6}
                data={importantPoints}
                style={{
                    data: {
                        fill: colors.primary,
                    },
                }}
            />
        );
    }

    renderImportantReference(point) {
        return (
            <VictoryAxis
                key={`${point.x}-${point.y}`}
                dependentAxis
                axisValue={point.x}
                style={{
                    axis: {
                        stroke: colors.primary,
                        strokeDasharray: '2,4',
                        strokeWidth: 2,
                    },
                    tickLabels: {
                        fill: 'none',
                    },
                }}
            />
        );
    }

    renderLines({ selectedProducts }) {
        return selectedProducts.map((product, i) => {
            return (
                <VictoryLine
                    key={product.id}
                    data={product.lineData}
                    style={{
                        data: {
                            stroke: this.getProductColor(product, i),
                            strokeWidth: product.selected ? 3 : 2,
                        },
                        labels: {
                            ...textStyles,
                        },
                    }}
                />
            );
        });
    }

    renderUserDensityReference({ selectedProducts, xLabelPosition }) {
        const sample = selectedProducts[0];

        if (!sample || !sample.userDensity) return;

        return (
            <VictoryAxis
                dependentAxis
                axisValue={sample.userDensity}
                style={{
                    axis: {
                        stroke: colors.primary,
                        strokeDasharray: '2,4',
                        strokeWidth: 2,
                    },
                    tickLabels: {
                        fill: 'none',
                    },
                }}
            />
        );
    }

    renderContainerComponent({ selectedProducts }) {
        const renderCursorLabel = ({ datum }) => {
            const targetX = datum.x - (datum.x % 500);
            const result = [
                `${_('Densidad')}: ${this.context.units.toDensity(targetX, 'corn').toString()}`,
                ...selectedProducts.map((product) => {
                    const estimatedYield = (product.lineData.find((point) => point.x === targetX) || {}).y;
                    return `${_('Rendimiento {{label}}', product)}: ${this.context.units
                        .toYield(estimatedYield, 'corn')
                        .toString()}`;
                }),
            ];
            return result.join('\n');
        };

        return (
            <VictoryCursorContainer
                cursorDimension='x'
                cursorLabel={renderCursorLabel}
                cursorLabelComponent={
                    <VictoryLabel
                        dx={5}
                        verticalAnchor='start'
                        style={[
                            { ...textStyles },
                            ...selectedProducts.map((product, i) => {
                                return { ...textStyles, fill: this.getProductColor(product, i) };
                            }),
                        ]}
                        backgroundStyle={Array.from({ length: selectedProducts.length + 1 }).fill({
                            fill: 'gray',
                            opacity: 0.1,
                            width: '250px',
                        })}
                    />
                }
                cursorComponent={
                    <LineSegment
                        style={{
                            stroke: colors.gray,
                            strokeDasharray: '2,4',
                            strokeWidth: 2,
                        }}
                    />
                }
            />
        );
    }
    render() {
        const mobile = isMobile();
        const { width: chartWidth, height: chartHeight } = this.state;

        const style = { width: `${chartWidth}px` };
        if (chartHeight) style.height = `${chartHeight}px`;
        const { selectedProducts, importantPoints, padding } = this.parseData(this.props.products);
        const { xLabelPosition, yLabelPosition } = this.calculateLabelPositions({ chartWidth, chartHeight, padding });

        return (
            <div style={style} ref={this.setRooNodeRef}>
                {Boolean(chartHeight) && Boolean(chartWidth) && (
                    <VictoryChart
                        width={chartWidth}
                        height={chartHeight}
                        padding={padding}
                        domainPadding={{ y: [0, 10], x: [0, mobile ? 70 : 120] }}
                        containerComponent={
                            mobile || (this.props.countryCode === 'BR' && !this.props.importantReferences)
                                ? undefined
                                : this.renderContainerComponent({ selectedProducts })
                        }
                    >
                        <VictoryLegend
                            x={chartWidth / 2 - padding.left}
                            y={chartHeight - 80}
                            title='Products'
                            centerTitle
                            orientation='horizontal'
                            gutter={20}
                            data={selectedProducts.map((product, i) => {
                                return {
                                    name: product.label,
                                    symbol: { fill: this.getProductColor(product, i) },
                                };
                            })}
                        />
                        {this.renderXAxis({ xLabelPosition })}
                        {this.renderYAxis({ yLabelPosition })}
                        {this.renderLines({ selectedProducts })}
                        {this.props.importantDots && this.renderImportantDots({ importantPoints })}
                        {this.props.importantReferences &&
                            importantPoints.map((point) => this.renderImportantReference(point))}
                        {this.renderUserDensityReference({ selectedProducts, xLabelPosition })}
                    </VictoryChart>
                )}
            </div>
        );
    }
}
